import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaIcon } from '../../FaIcon';
import { useAgentSessions } from './AgentSessionContext';
import { SessionFilters } from './SessionFilters';
import { SessionList } from './SessionList';
import { ShareSettingsModal } from './ShareSettingsModal';
import { DeleteSessionModal } from './DeleteSessionModal';
import { AgentToast } from './AgentToast';

const SIDEBAR_MIN = 220;
const SIDEBAR_MAX = 520;
const SIDEBAR_DEFAULT = 340;
const STORAGE_KEY = 'agent_sidebar_width';

export const UnifiedAgentSidebar = ({ clients, onNewChat }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        entryPoint, clientId, activeSessionId,
        showArchived, setShowArchived, archivedCount,
        archiveSession, unarchiveSession, deleteSession,
        toast, showToast, dismissToast,
    } = useAgentSessions();

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [shareModal, setShareModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);

    // Resizable width
    const [sidebarWidth, setSidebarWidth] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? Math.min(Math.max(parseInt(saved, 10), SIDEBAR_MIN), SIDEBAR_MAX) : SIDEBAR_DEFAULT;
    });
    const isResizing = useRef(false);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        isResizing.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const onMouseMove = (ev) => {
            if (!isResizing.current) return;
            // El sidebar está pegado a la izquierda del .agent-area, que tiene un offset por el LayoutSidebar (4.5em ≈ 72px)
            const parentLeft = document.querySelector('.agent-area')?.getBoundingClientRect().left || 0;
            const newWidth = Math.min(Math.max(ev.clientX - parentLeft, SIDEBAR_MIN), SIDEBAR_MAX);
            setSidebarWidth(newWidth);
        };

        const onMouseUp = () => {
            isResizing.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            // Persist
            setSidebarWidth(w => {
                localStorage.setItem(STORAGE_KEY, String(w));
                return w;
            });
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, []);

    const getNewChatPath = () => {
        if (entryPoint === 'client') return `/client/${clientId}/agent`;
        if (entryPoint === 'portal') return `/portal/agent`;
        return '/agent/chat';
    };

    const handleNewChat = (e) => {
        e.preventDefault();
        if (!activeSessionId) {
            if (onNewChat) onNewChat();
        } else {
            navigate(getNewChatPath());
        }
    };

    const handleArchive = useCallback((session) => {
        if (session.is_archived) {
            unarchiveSession(session.session_id);
            showToast(t('agent_chat.unarchived_toast'));
        } else {
            archiveSession(session.session_id, true);
            showToast(
                t('agent_chat.archived_toast'),
                () => unarchiveSession(session.session_id)
            );
        }
    }, [archiveSession, unarchiveSession, showToast, t]);

    const handleDeleteConfirm = useCallback(() => {
        if (!deleteModal) return;
        const sessionId = deleteModal.session_id;
        deleteSession(sessionId).then(() => {
            setDeleteModal(null);
            if (activeSessionId === sessionId) {
                navigate(getNewChatPath());
            }
        });
    }, [deleteModal, deleteSession, activeSessionId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    const style = isCollapsed ? undefined : { width: sidebarWidth };

    return (
        <div className={`agent-sidebar ${isCollapsed ? 'agent-sidebar--collapsed' : ''}`} style={style}>
            <div className="agent-sidebar__header">
                <div className="agent-sidebar__header-row">
                    <a href={getNewChatPath()} className="btn -alt agent-sidebar__new-chat" onClick={handleNewChat}>
                        <FaIcon iconName="plus" />
                        {!isCollapsed && <span className="agent-sidebar__new-chat-text">{t('agent_chat.new_chat')}</span>}
                    </a>
                    <button
                        className="agent-sidebar__collapse-btn"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        title={t(isCollapsed ? 'agent_chat.expand_sessions' : 'agent_chat.collapse_sessions')}
                        aria-label={t(isCollapsed ? 'agent_chat.expand_sessions' : 'agent_chat.collapse_sessions')}
                    >
                        <FaIcon iconName={isCollapsed ? 'chevron-right' : 'chevron-left'} />
                    </button>
                </div>
            </div>

            {!isCollapsed && (
                <>
                    <SessionFilters clients={clients} />
                    <SessionList
                        onShare={(session) => setShareModal(session)}
                        onArchive={handleArchive}
                        onDelete={(session) => setDeleteModal(session)}
                    />
                    {archivedCount > 0 && (
                        <div className="agent-sidebar__footer">
                            <label className="agent-sidebar__archive-toggle">
                                <input
                                    type="checkbox"
                                    checked={showArchived}
                                    onChange={(e) => setShowArchived(e.target.checked)}
                                />
                                <span>{t('agent_chat.show_archived')} ({archivedCount})</span>
                            </label>
                        </div>
                    )}
                </>
            )}

            {/* Resize handle */}
            {!isCollapsed && (
                <div className="agent-sidebar__resize-handle" onMouseDown={handleMouseDown} />
            )}

            {shareModal && (
                <ShareSettingsModal
                    session={shareModal}
                    onClose={() => setShareModal(null)}
                />
            )}

            {deleteModal && (
                <DeleteSessionModal
                    session={deleteModal}
                    onConfirm={handleDeleteConfirm}
                    onClose={() => setDeleteModal(null)}
                />
            )}

            {toast && (
                <AgentToast
                    message={toast.message}
                    undoAction={toast.undoAction}
                    onDismiss={dismissToast}
                />
            )}
        </div>
    );
};
